import {
  Menu,
  X,
  User as UserIcon,
  LogOut,
  Settings,
  Users,
  LayoutDashboard,
} from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "@/shared/components/ui/sheet";
import { Button } from "@/shared/components/ui/button";
import { Separator } from "@/shared/components/ui/separator";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/shared/components/ui/avatar";
import { navigationItems } from "./NavigationData";
import { useAuth } from "@/shared/hooks/useAuth";
import { useBranding } from "@/shared/contexts/BrandingContext";
import type { User } from "@shared/schema/types";
import { logout } from "@/shared/utils/queryClient";

interface MobileMenuProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  preserveBrandUrl: (href: string) => string;
}

export default function MobileMenu({
  isOpen,
  setIsOpen,
  preserveBrandUrl,
}: MobileMenuProps) {
  const { user, isLoading } = useAuth();
  const { isAegis360 } = useBranding();
  const typedUser = user as User | null | undefined;

  const handleLogin = () => {
    window.location.href = "/login";
  };

  const handleLogout = async () => {
    await logout();
    window.location.href = "/login";
  };

  const getInitials = (firstName?: string, lastName?: string) => {
    if (!firstName && !lastName) return "U";
    return `${firstName?.[0] || ""}${lastName?.[0] || ""}`.toUpperCase();
  };

  const getUserDisplayName = () => {
    if (!typedUser) return "";
    if (typedUser.firstName && typedUser.lastName) {
      return `${typedUser.firstName} ${typedUser.lastName}`;
    }
    if (typedUser.firstName) return typedUser.firstName;
    if (typedUser.email) return typedUser.email.split("@")[0];
    return "User";
  };

  const isB2BUser = typedUser && typedUser.companyId;

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild className="lg:hidden">
        <Button
          variant="ghost"
          size="icon"
          className="text-gray-700"
          data-testid="button-mobile-menu"
        >
          <Menu className="h-6 w-6" />
          <span className="sr-only">Open menu</span>
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-[300px] sm:w-[400px]">
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between p-6 border-b">
            <h2 className="text-lg font-semibold">Menu</h2>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsOpen(false)}
              className="h-6 w-6 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          <div className="flex-1 overflow-auto">
            <nav className="p-6 space-y-4">
              {navigationItems.map((item) => (
                <div key={item.title}>
                  {item.items ? (
                    <div className="space-y-2">
                      <div className="font-medium text-gray-900 pb-2 border-b">
                        {item.title}
                      </div>
                      {item.items.map((subItem) => (
                        <a
                          key={subItem.title}
                          href={preserveBrandUrl(subItem.href)}
                          className="block pl-4 py-2 text-sm text-gray-600 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors"
                          onClick={() => setIsOpen(false)}
                        >
                          <div className="flex items-center space-x-2">
                            <span>{subItem.icon}</span>
                            <span>{subItem.title}</span>
                          </div>
                        </a>
                      ))}
                    </div>
                  ) : (
                    <a
                      href={preserveBrandUrl(item.href)}
                      className="block py-2 font-medium text-gray-900 hover:text-blue-700 transition-colors"
                      onClick={() => setIsOpen(false)}
                    >
                      {item.title}
                    </a>
                  )}
                </div>
              ))}
            </nav>
          </div>

          {/* Auth Section */}
          <div className="p-6 border-t">
            {isLoading ? (
              <div className="h-12 bg-gray-200 animate-pulse rounded" />
            ) : !user ? (
              <div className="space-y-3" data-testid="mobile-auth-logged-out">
                <Button
                  onClick={() => {
                    window.location.href = "/backend-admin";
                    setIsOpen(false);
                  }}
                  className={`w-full justify-start border-2 ${
                    isAegis360
                      ? "border-blue-600 text-blue-600 hover:bg-blue-50"
                      : "border-emerald-600 text-emerald-600 hover:bg-emerald-50"
                  }`}
                  variant="outline"
                  data-testid="button-mobile-backend-access"
                >
                  <svg
                    className="mr-2 h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  Backend Admin
                </Button>
                <Button
                  onClick={handleLogin}
                  variant="outline"
                  className="w-full justify-start"
                  data-testid="button-mobile-login"
                >
                  <UserIcon className="mr-2 h-4 w-4" />
                  Login
                </Button>
                <Button
                  onClick={handleLogin}
                  className={
                    isAegis360
                      ? "w-full bg-blue-600 hover:bg-blue-700"
                      : "w-full bg-green-600 hover:bg-green-700"
                  }
                  data-testid="button-mobile-get-started"
                >
                  Get Started
                </Button>
              </div>
            ) : (
              <div className="space-y-3" data-testid="mobile-auth-logged-in">
                <div className="flex items-center space-x-3 pb-3 border-b">
                  <Avatar className="h-10 w-10">
                    <AvatarImage
                      src={typedUser?.profileImageUrl ?? undefined}
                      alt={getUserDisplayName()}
                    />
                    <AvatarFallback
                      className={
                        isAegis360
                          ? "bg-blue-100 text-blue-700"
                          : "bg-green-100 text-green-700"
                      }
                    >
                      {getInitials(typedUser?.firstName, typedUser?.lastName)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p
                      className="text-sm font-medium text-gray-900 truncate"
                      data-testid="mobile-text-user-name"
                    >
                      {getUserDisplayName()}
                    </p>
                    <p
                      className="text-xs text-gray-500 truncate"
                      data-testid="mobile-text-user-email"
                    >
                      {typedUser?.email}
                    </p>
                  </div>
                </div>

                <a
                  href="/dashboard"
                  className="flex items-center space-x-2 py-2 text-sm text-gray-700 hover:text-blue-700"
                  onClick={() => setIsOpen(false)}
                  data-testid="mobile-link-dashboard"
                >
                  <LayoutDashboard className="h-4 w-4" />
                  <span>Dashboard</span>
                </a>

                <a
                  href="/profile"
                  className="flex items-center space-x-2 py-2 text-sm text-gray-700 hover:text-blue-700"
                  onClick={() => setIsOpen(false)}
                  data-testid="mobile-link-my-account"
                >
                  <UserIcon className="h-4 w-4" />
                  <span>My Account</span>
                </a>

                {isB2BUser && (
                  <>
                    <Separator />
                    <a
                      href="/organization/settings"
                      className="flex items-center space-x-2 py-2 text-sm text-gray-700 hover:text-blue-700"
                      onClick={() => setIsOpen(false)}
                      data-testid="mobile-link-company-settings"
                    >
                      <Settings className="h-4 w-4" />
                      <span>Company Settings</span>
                    </a>

                    {(typedUser?.isOrgAdmin || typedUser?.canInviteUsers) && (
                      <a
                        href="/organization/team"
                        className="flex items-center space-x-2 py-2 text-sm text-gray-700 hover:text-blue-700"
                        onClick={() => setIsOpen(false)}
                        data-testid="mobile-link-team-management"
                      >
                        <Users className="h-4 w-4" />
                        <span>Team Management</span>
                      </a>
                    )}
                  </>
                )}

                <Separator />

                <a
                  href="/backend-admin"
                  className={`flex items-center space-x-2 py-2 text-sm font-medium ${
                    isAegis360 ? "text-blue-600" : "text-emerald-600"
                  }`}
                  onClick={() => setIsOpen(false)}
                  data-testid="mobile-link-backend-admin"
                >
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  <span>Backend Admin</span>
                </a>

                <Button
                  onClick={handleLogout}
                  variant="outline"
                  className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
                  data-testid="button-mobile-logout"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Logout
                </Button>
              </div>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
